import React from 'react';
import styled from 'styled-components';
import { Body3, Body5, Container } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { ModeContext } from '@/contexts/ModeContext';

import { UploadPhase } from './UploadPhase';
import { ReadyPhase } from './ReadyPhase';
import { ResultsPhase } from './ResultsPhase';
import { CreateTemplatePhase } from './CreateTemplatePhase';
import { EditPhase } from './EditPhase';
import { RfpTemplateResultsProvider } from './RfpTemplateResultsContext';

const PHASES = {
  MODE_SELECT: 'MODE_SELECT',
  UPLOAD: 'UPLOAD',
  READY: 'READY',
  CREATE_TEMPLATE: 'CREATE_TEMPLATE',
  EDIT: 'EDIT',
  RESULTS: 'RESULTS',
};

const ModeOption = styled.div`
  flex: 1;
  padding: 24px;
  border: 2px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 8px;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.text};
  }
`;

const ModeOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 440px;
`;

function ModeSelectPhase({ onSelectMode }) {
  return (
    <Container top={24}>
      <Body5 bold>Select an option</Body5>
      <Container top={32}>
        <ModeOptionsWrapper>
          <ModeOption onClick={() => onSelectMode(PHASES.UPLOAD)}>
            <Container>
              <Body3 bold>Create Template From Document or Deck</Body3>
            </Container>
            <Container top={4}>
              <Body5>Upload a Word or PowerPoint document to create a new report template.</Body5>
            </Container>
          </ModeOption>
          <ModeOption onClick={() => onSelectMode(PHASES.CREATE_TEMPLATE)}>
            <Container>
              <Body3 bold>Create Template</Body3>
            </Container>
            <Container top={4}>
              <Body5>Create a custom template from scratch.</Body5>
            </Container>
          </ModeOption>
          <ModeOption onClick={() => onSelectMode(PHASES.READY)}>
            <Container>
              <Body3 bold>Generate Report</Body3>
            </Container>
            <Container top={4}>
              <Body5>Generate responses from a previously uploaded template.</Body5>
            </Container>
          </ModeOption>
          <ModeOption onClick={() => onSelectMode(PHASES.EDIT)}>
            <Container>
              <Body3 bold>Edit Template</Body3>
            </Container>
            <Container top={4}>
              <Body5>Edit template fields for a previously uploaded template.</Body5>
            </Container>
          </ModeOption>
        </ModeOptionsWrapper>
        <Container top={24} />
      </Container>
    </Container>
  );
}

export function TemplateProcessorTool({ onGoBack, isStreamingEnabled: initialIsStreamingEnabled = true, modeContext }) {
  const [phase, setPhase] = React.useState(PHASES.MODE_SELECT);
  const [resultId, setResultId] = React.useState(null);
  const [streamingId, setStreamingId] = React.useState(null);
  const [agentRecordId, setAgentRecordId] = React.useState(null);
  const [templateRunId, setTemplateRunId] = React.useState(null);
  const [templateId, setTemplateId] = React.useState(null);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [sourceDocIds, setSourceDocIds] = React.useState([]);
  const [queryListMetadata, setQueryListMetadata] = React.useState([]);
  const [isStreamingEnabled, setIsStreamingEnabled] = React.useState(initialIsStreamingEnabled);
  const [editEntryTemplate, setEditEntryTemplate] = React.useState(null);

  const handleUploadComplete = React.useCallback(() => {
    setPhase(PHASES.READY);
  }, []);

  const handleGenerate = React.useCallback(
    ({ resultId, streamingId, agentRecordId, templateRunId, queryListMetadata, sourceDocIds = [] }) => {
      setResultId(resultId);
      setStreamingId(streamingId);
      setAgentRecordId(agentRecordId);
      setTemplateRunId(templateRunId);
      setSourceDocIds(sourceDocIds);
      setQueryListMetadata(queryListMetadata || []);
      setPhase(PHASES.RESULTS);
    },
    []
  );

  const handleTemplateSelect = React.useCallback((template) => {
    setSelectedTemplate(template || null);
    setTemplateId(template?.id || null);
  }, []);

  const handleSelectMode = React.useCallback((selectedPhase) => {
    setPhase(selectedPhase);
  }, []);

  const handleCreateTemplateSuccess = React.useCallback((entry) => {
    setEditEntryTemplate(entry);
    setPhase(PHASES.EDIT);
  }, []);

  const handleReset = React.useCallback(() => {
    setPhase(PHASES.MODE_SELECT);
    setResultId(null);
    setStreamingId(null);
    setAgentRecordId(null);
    setTemplateRunId(null);
    setTemplateId(null);
    setSelectedTemplate(null);
    setSourceDocIds([]);
    setQueryListMetadata([]);
    setEditEntryTemplate(null);
  }, []);

  return (
    <ModeContext.Provider value={modeContext}>
      <>
        <BackArrowButton onClick={phase === PHASES.MODE_SELECT ? onGoBack : handleReset} />
        {phase === PHASES.MODE_SELECT && <ModeSelectPhase onSelectMode={handleSelectMode} />}
        {phase === PHASES.UPLOAD && <UploadPhase onUploadComplete={handleUploadComplete} />}
        {phase === PHASES.READY && (
          <ReadyPhase
            isStreamingEnabled={isStreamingEnabled}
            onStreamingEnabledChange={setIsStreamingEnabled}
            onGenerate={handleGenerate}
            onTemplateSelect={handleTemplateSelect}
            onGoToUpload={() => setPhase(PHASES.UPLOAD)}
          />
        )}
        {phase === PHASES.CREATE_TEMPLATE && <CreateTemplatePhase onCreated={handleCreateTemplateSuccess} />}
        {phase === PHASES.EDIT && (
          <EditPhase entryTemplate={editEntryTemplate} onGoToUpload={() => setPhase(PHASES.UPLOAD)} />
        )}
        {phase === PHASES.RESULTS && (
          <RfpTemplateResultsProvider
            isStreamingEnabled={isStreamingEnabled}
            resultId={resultId}
            streamingId={streamingId}
            agentRecordId={agentRecordId}
            templateRunId={templateRunId}
            templateId={templateId}
            selectedTemplate={selectedTemplate}
            sourceDocIds={sourceDocIds}
            queryListMetadata={queryListMetadata}
            onReset={handleReset}
          >
            <ResultsPhase />
          </RfpTemplateResultsProvider>
        )}
      </>
    </ModeContext.Provider>
  );
}
